type ClassValue = string | number | boolean | undefined | null | Record<string, boolean | undefined | null> | ClassValue[];

export function cn(...inputs: ClassValue[]) {
  return inputs.map(input => {
    if (typeof input === 'string') return input;
    if (Array.isArray(input)) return input.filter(Boolean).join(' ');
    if (typeof input === 'object' && input !== null) {
      return Object.entries(input)
        .filter(([, value]) => Boolean(value))
        .map(([key]) => key)
        .join(' ');
    }
    return '';
  }).filter(Boolean).join(' ');
}

export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');
}

export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

export function getReadTime(content: string): number {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
}

export const TIME_SLOTS = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
];

export const SERVICES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'project-discussion', label: 'Project Discussion' },
  { value: 'tech-interview-prep', label: 'Tech Interview Prep' },
  { value: 'web-development', label: 'Web Development' },
  { value: 'api-development', label: 'API Development' },
  { value: 'other', label: 'Other' },
];

export const SKILLS = {
  frontend: [
    { name: 'React.js', percentage: 90, icon: '/images/skills/react.svg' },
    { name: 'Next.js', percentage: 88, icon: '/images/skills/nextjs.svg' },
    { name: 'TypeScript', percentage: 85, icon: '/images/skills/typescript.svg' },
    { name: 'JavaScript', percentage: 92, icon: '/images/skills/javascript.svg' },
    { name: 'Tailwind CSS', percentage: 90, icon: '/images/skills/tailwind.svg' },
    { name: 'HTML/CSS', percentage: 95, icon: '/images/skills/html.svg' },
  ],
  backend: [
    { name: 'Laravel', percentage: 90, icon: '/images/skills/laravel.svg' },
    { name: 'Node.js', percentage: 82, icon: '/images/skills/nodejs.svg' },
    { name: 'PHP', percentage: 88, icon: '/images/skills/php.svg' },
    { name: 'MySQL', percentage: 85, icon: '/images/skills/mysql.svg' },
    { name: 'PostgreSQL', percentage: 80, icon: '/images/skills/postgresql.svg' },
    { name: 'REST APIs', percentage: 90, icon: '/images/skills/api.svg' },
  ],
  tools: [
    { name: 'Git & GitHub', percentage: 88, icon: '/images/skills/git.svg' },
    { name: 'Docker', percentage: 70, icon: '/images/skills/docker.svg' },
    { name: 'VS Code', percentage: 95, icon: '/images/skills/vscode.svg' },
    { name: 'Figma', percentage: 75, icon: '/images/skills/figma.svg' },
    { name: 'Linux', percentage: 78, icon: '/images/skills/linux.svg' },
    { name: 'Vercel', percentage: 85, icon: '/images/skills/vercel.svg' },
  ],
};

export const SOCIAL_LINKS = [
  { name: 'GitHub', url: 'https://github.com/nasifniloy', icon: 'github' },
  { name: 'LinkedIn', url: 'https://linkedin.com/in/nasifniloy', icon: 'linkedin' },
  { name: 'Twitter', url: 'https://twitter.com/nasifniloy', icon: 'twitter' },
  { name: 'Instagram', url: 'https://instagram.com/nasifniloy', icon: 'instagram' },
];

export const PROJECTS_DATA = [
  {
    id: '1',
    title: 'E-Commerce Platform',
    slug: 'e-commerce-platform',
    description: 'Full-stack e-commerce solution with real-time inventory, payment processing, and admin dashboard.',
    techStack: ['Next.js', 'TypeScript', 'Prisma', 'Stripe', 'Tailwind CSS'],
    images: ['/images/projects/ecommerce.jpg'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/nasifniloy/ecommerce',
    featured: true,
    category: 'Full-Stack',
  },
  {
    id: '2',
    title: 'Task Management App',
    slug: 'task-management-app',
    description: 'Collaborative project management tool with real-time updates and team features.',
    techStack: ['React', 'Node.js', 'Socket.io', 'MongoDB', 'Material UI'],
    images: ['/images/projects/taskmanager.jpg'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/nasifniloy/taskmanager',
    featured: true,
    category: 'React',
  },
  {
    id: '3',
    title: 'Blog CMS Platform',
    slug: 'blog-cms-platform',
    description: 'Content management system with rich text editor, SEO tools, and analytics dashboard.',
    techStack: ['Laravel', 'Vue.js', 'MySQL', 'Redis', 'Bootstrap'],
    images: ['/images/projects/blogcms.jpg'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/nasifniloy/blogcms',
    featured: true,
    category: 'Laravel',
  },
  {
    id: '4',
    title: 'Real Estate Listing',
    slug: 'real-estate-listing',
    description: 'Property listing platform with advanced search, maps integration, and virtual tours.',
    techStack: ['Next.js', 'Prisma', 'PostgreSQL', 'Mapbox', 'Tailwind CSS'],
    images: ['/images/projects/realestate.jpg'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/nasifniloy/realestate',
    featured: true,
    category: 'Next.js',
  },
  {
    id: '5',
    title: 'Social Media Dashboard',
    slug: 'social-media-dashboard',
    description: 'Analytics dashboard for social media management with scheduling and reporting.',
    techStack: ['React', 'Chart.js', 'Express', 'MongoDB', 'Tailwind CSS'],
    images: ['/images/projects/social.jpg'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/nasifniloy/socialdash',
    featured: true,
    category: 'React',
  },
  {
    id: '6',
    title: 'Restaurant POS System',
    slug: 'restaurant-pos-system',
    description: 'Point of sale system for restaurants with order management and kitchen display.',
    techStack: ['Laravel', 'React', 'MySQL', 'Socket.io', 'Tailwind CSS'],
    images: ['/images/projects/pos.jpg'],
    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com/nasifniloy/restaurantpos',
    featured: true,
    category: 'Full-Stack',
  },
];

export const TESTIMONIALS = [
  {
    name: 'Sarah Johnson',
    designation: 'CEO',
    company: 'TechStart Inc.',
    avatar: '/images/testimonials/avatar1.jpg',
    rating: 5,
    quote: 'Nasif delivered an exceptional e-commerce platform that exceeded our expectations. His attention to detail and technical expertise are outstanding.',
  },
  {
    name: 'Michael Chen',
    designation: 'Product Manager',
    company: 'InnovateLab',
    avatar: '/images/testimonials/avatar2.jpg',
    rating: 5,
    quote: 'Working with Nasif was a great experience. He understood our requirements perfectly and delivered a high-quality solution on time.',
  },
  {
    name: 'Emily Williams',
    designation: 'Founder',
    company: 'DesignCraft Studio',
    avatar: '/images/testimonials/avatar3.jpg',
    rating: 5,
    quote: 'Nasif built us a stunning portfolio website with amazing animations. His React and Next.js skills are top-notch.',
  },
  {
    name: 'David Park',
    designation: 'CTO',
    company: 'DataFlow Systems',
    avatar: '/images/testimonials/avatar4.jpg',
    rating: 4,
    quote: 'Excellent developer with strong problem-solving skills. The API architecture he designed for us was clean and scalable.',
  },
];

export const TIMELINE_DATA = {
  experience: [
    {
      title: 'Senior Full-Stack Developer',
      company: 'Tech Company',
      period: '2023 - Present',
      description: 'Leading development of web applications using Next.js and Laravel. Managing a team of 5 developers.',
      techStack: ['Next.js', 'Laravel', 'PostgreSQL', 'Docker'],
    },
    {
      title: 'Full-Stack Developer',
      company: 'Digital Agency',
      period: '2021 - 2023',
      description: 'Built and maintained client websites and web applications. Focused on React and Laravel projects.',
      techStack: ['React', 'Laravel', 'MySQL', 'AWS'],
    },
    {
      title: 'Junior Web Developer',
      company: 'Startup Inc.',
      period: '2020 - 2021',
      description: 'Started career building responsive websites and learning modern web technologies.',
      techStack: ['HTML/CSS', 'JavaScript', 'PHP', 'WordPress'],
    },
  ],
  education: [
    {
      title: 'B.Sc. in Computer Science',
      company: 'University of Dhaka',
      period: '2018 - 2022',
      description: 'Graduated with honors. Focused on software engineering and web technologies.',
      techStack: ['Data Structures', 'Algorithms', 'Database Systems'],
    },
  ],
};

export const SERVICES_DATA = [
  {
    title: 'Web Development',
    description: 'Custom web applications built with modern frameworks like React, Next.js, and Laravel.',
    icon: 'globe',
  },
  {
    title: 'API Development',
    description: 'RESTful and GraphQL APIs designed for performance, security, and scalability.',
    icon: 'code',
  },
  {
    title: 'E-Commerce Solutions',
    description: 'Full-featured online stores with payment processing, inventory, and order management.',
    icon: 'shopping-cart',
  },
  {
    title: 'UI/UX Design',
    description: 'Beautiful, intuitive interfaces with smooth animations and excellent user experience.',
    icon: 'palette',
  },
  {
    title: 'CMS Development',
    description: 'Custom content management systems tailored to your specific business needs.',
    icon: 'layout',
  },
  {
    title: 'Tech Consulting',
    description: 'Technical guidance on architecture, stack selection, and development best practices.',
    icon: 'lightbulb',
  },
];
